import { ReactNode } from "react";
import { Close as CloseIcon } from "@mui/icons-material";
import { Box, Theme, alpha, Modal, SxProps, useTheme, IconButton, Typography, Backdrop } from "@mui/material";


interface ModalContainerProps {
    open: boolean;
    title?: string;
    minHeight?: string;
    onClose: () => void;
    children: ReactNode;
    showCloseButton?: boolean;
    maxWidth?: number | string;
    contentSx?: SxProps<Theme>;
    maxHeight?: number | string;
    containerSx?: SxProps<Theme>;
    backdropBlur?: number | string;
}

export function ThemedModalContainer({
    open,
    title,
    onClose,
    children,
    contentSx,
    containerSx,
    maxWidth = 800,
    maxHeight = '90vh',
    minHeight = '40dvh',
    backdropBlur = '8px',
    showCloseButton = true,
}: ModalContainerProps) {
    const theme = useTheme();

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            aria-labelledby={title ? "modal-title" : undefined}
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: `blur(${backdropBlur})`,
                        backgroundColor: alpha(theme.palette.background.default, 0.7),
                    }
                }
            }}
        >
            <Box
                sx={[
                    {
                        p: 0.5,
                        top: '50%',
                        left: '50%',
                        width: '90%',
                        opacity: 0.9,
                        borderRadius: 4,
                        display: 'flex',
                        overflow: 'visible',
                        maxWidth: maxWidth,
                        minHeight: minHeight,
                        maxHeight: maxHeight,
                        position: 'absolute',
                        flexDirection: 'column',
                        backdropFilter: 'blur(4px)',
                        bgcolor: 'background.paper',
                        transform: 'translate(-50%, -50%)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                        transition: theme.transitions.create(['transform', 'box-shadow'], { duration: theme.transitions.duration.shorter, }),
                        '&:hover': {
                            border: `2px solid ${alpha(theme.palette.divider, 0.5)}`,
                        }
                    },
                    ...(Array.isArray(containerSx) ? containerSx : [containerSx])
                ]}
            >
                {title && (
                    <Box
                        sx={{
                            gap: 2,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            px: { xs: 2, sm: 4 },
                            pt: { xs: 2, sm: 4 },
                            py: { xs: 1.5, sm: 2 },
                            bgcolor: 'background.paper',
                            justifyContent: 'space-between',
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }}
                    >
                        <Typography
                            variant="h5"
                            component="h2"
                            fontWeight={600}
                            sx={{
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.main}80)`,
                                WebkitTextFillColor: 'transparent',
                                color: theme.palette.primary.main,
                                WebkitBackgroundClip: 'text',
                                textOverflow: "ellipsis",
                                backgroundClip: 'text',
                                wordBreak: 'break-word',
                                whiteSpace: "nowrap",
                                marginBottom: 0.5,
                                overflow: "hidden",
                                display: "block",
                                fontWeight: 700,
                                flex: 1,
                            }}
                        >
                            {title}
                        </Typography>
                        {showCloseButton && (
                            <IconButton
                                size="small"
                                onClick={onClose}
                                sx={{
                                    ml: 'auto',
                                    flexShrink: 0,
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.5),
                                        transform: 'scale(1.1)',
                                    },
                                    transition: theme.transitions.create(['transform', 'background-color'], {
                                        duration: theme.transitions.duration.shorter,
                                    }),
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Box>
                )}

                <Box
                    sx={[
                        {
                            width: "100%",
                            overflow: 'auto',
                            alignSelf: "center",
                            p: { xs: 2, sm: 4 },
                        },
                        ...(Array.isArray(contentSx) ? contentSx : [contentSx])
                    ]}
                >
                    {children}
                </Box>

                {!title && showCloseButton && (
                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{
                            zIndex: 1000,
                            position: 'absolute',
                            top: { xs: 15, sm: 25 },
                            right: { xs: 15, sm: 25 },
                            bgcolor: alpha(theme.palette.primary.main, 0.5),
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.9),
                                transform: 'scale(1.1)',
                            },
                            transition: theme.transitions.create(['transform', 'background-color'], {
                                duration: theme.transitions.duration.shorter,
                            }),
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>
        </Modal>
    );
}